import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth.store';

// withCredentials is a no-op while the API is same-origin (relative baseURL), which
// it is today — kept so the refresh cookie still travels if that ever changes.
const api = axios.create({ baseURL: '', withCredentials: true });

api.interceptors.request.use((config) => {
  const { token } = useAuthStore.getState();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise: Promise<string> | null = null;

/**
 * Mint an access token from the refresh cookie. Also restores `partner`, which is
 * how a reloaded tab recovers its identity now that nothing is persisted.
 * Concurrent callers share one in-flight request.
 */
export function refreshSession(): Promise<string> {
  if (!refreshPromise) {
    // Bare axios, not `api`: a 401 here must not re-enter the response interceptor
    // and call this again. isAuthEndpoint guards that too, but a structural
    // guarantee beats a conditional one when the failure mode is an endless loop.
    refreshPromise = axios
      .post('/partner/auth/refresh', {}, { withCredentials: true })
      .then(({ data }) => {
        const token = data?.data?.accessToken;
        if (!token) throw new Error('No access token in refresh response');
        const partner = data.data.partner;
        if (partner) useAuthStore.getState().setAuth(partner, token);
        else useAuthStore.getState().setAccessToken(token);
        return token;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (res) => res,
  async (err: AxiosError) => {
    const originalRequest = err.config as InternalAxiosRequestConfig & { _retried?: boolean };

    // Never try to refresh off a failing auth call — that loops.
    const isAuthEndpoint = (originalRequest?.url || '').includes('/partner/auth/');

    if (err.response?.status === 401 && originalRequest && !originalRequest._retried && !isAuthEndpoint) {
      originalRequest._retried = true;

      try {
        const newToken = await refreshSession();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        useAuthStore.getState().clear();
        window.location.href = '/login';
        return Promise.reject(err);
      }
    }

    // Not for auth endpoints: a 401 from /partner/auth/login means bad credentials,
    // and redirecting hard-reloads the page before LoginPage can render the error.
    if (err.response?.status === 401 && !isAuthEndpoint) {
      useAuthStore.getState().clear();
      window.location.href = '/login';
    }

    // Surface the API's message rather than "Request failed with status code 401",
    // which is what the login form would otherwise show.
    const body = err.response?.data as { error?: { message?: string } } | undefined;
    const message = body?.error?.message;
    return Promise.reject(message ? new Error(message) : err);
  },
);

export default api;
